import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import type {
    ResolvedTemplateDesign,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { StudioCleanRuntime } from "./StudioCleanRuntime";

function StudioCleanStyles() {
    return (
        <style>{`
            [data-web-template="studio-clean"] {
                background:
                    linear-gradient(rgba(17,19,21,.028) 1px, transparent 1px),
                    var(--site-background);
                background-size: 100% 64px, auto;
                isolation: isolate;
                scroll-behavior: smooth;
                scroll-padding-top: 112px;
            }

            [data-web-template="studio-clean"] ::selection {
                background: var(--site-accent);
                color: var(--site-primary);
            }

            [data-web-template="studio-clean"] :focus-visible {
                outline: 3px solid var(--site-accent);
                outline-offset: 4px;
            }

            [data-web-template="studio-clean"] *,
            [data-web-template="studio-clean"] *::before,
            [data-web-template="studio-clean"] *::after {
                box-sizing: border-box;
            }

            [data-web-template="studio-clean"] .site-builder-section,
            [data-web-template="studio-clean"] [data-site-builder-block="footer"] {
                padding-inline: clamp(1.25rem, 2.8vw, 2.5rem);
                position: relative;
            }

            [data-web-template="studio-clean"] .site-builder-grid {
                display: grid;
                gap: clamp(1.25rem, 2.8vw, 2.75rem);
                margin-inline: auto;
                width: min(100%, var(--site-container));
            }

            [data-web-template="studio-clean"] .site-builder-grid:not(.site-builder-grid-placed) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
            }

            [data-web-template="studio-clean"] .site-builder-grid:not(.site-builder-grid-placed) > .site-builder-grid-item {
                grid-column: 1 / -1;
            }

            [data-web-template="studio-clean"] .site-builder-column {
                align-content: start;
                display: grid;
                gap: 1.1rem;
                min-width: 0;
            }

            [data-web-template="studio-clean"] .site-builder-column-inline {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
            }

            [data-web-template="studio-clean"] .site-builder-text-h1,
            [data-web-template="studio-clean"] .site-builder-text-h2,
            [data-web-template="studio-clean"] .site-builder-text-h3 {
                font-stretch: condensed;
                text-wrap: balance;
            }

            [data-web-template="studio-clean"] .site-builder-text-h1,
            [data-web-template="studio-clean"] .site-builder-text-h2 {
                text-transform: uppercase;
            }

            [data-web-template="studio-clean"] .site-builder-text-h1 {
                line-height: .8 !important;
            }

            [data-web-template="studio-clean"] .site-builder-text-h2 {
                line-height: .86 !important;
            }

            [data-web-template="studio-clean"] .site-builder-button {
                letter-spacing: .1em;
                text-transform: uppercase;
                transition:
                    background-color 180ms ease,
                    border-color 180ms ease,
                    box-shadow 220ms ease,
                    color 180ms ease,
                    transform 220ms cubic-bezier(.2,.75,.25,1);
            }

            [data-web-template="studio-clean"] .site-builder-button:hover {
                box-shadow: 6px 6px 0 color-mix(in srgb, var(--site-accent) 38%, transparent);
                transform: translate(-2px, -2px);
            }

            [data-web-template="studio-clean"] .site-builder-card {
                transition:
                    border-color 220ms ease,
                    box-shadow 260ms ease,
                    transform 260ms cubic-bezier(.2,.75,.25,1);
            }

            [data-web-template="studio-clean"] .site-builder-card:hover {
                border-color: var(--site-accent) !important;
                box-shadow: 8px 10px 0 color-mix(in srgb, var(--site-accent) 24%, transparent);
                transform: translateY(-4px);
            }

            [data-web-template="studio-clean"] .site-builder-navigation {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
                gap: clamp(.45rem, .8vw, .8rem);
                justify-content: center;
            }

            [data-web-template="studio-clean"] .site-builder-navigation-item,
            [data-web-template="studio-clean"] .site-builder-navigation-link {
                display: inline-flex;
            }

            [data-web-template="studio-clean"] [data-web-region="studioServiceStrip"] {
                left: 0;
                position: sticky;
                right: 0;
                top: 0;
                z-index: 72;
            }

            [data-web-template="studio-clean"] [data-web-region="studioServiceStrip"] > .site-builder-grid,
            [data-web-template="studio-clean"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
            }

            [data-web-template="studio-clean"] [data-web-region="header"] {
                position: sticky;
                top: 36px;
                transition:
                    background-color 220ms ease,
                    box-shadow 220ms ease,
                    min-height 220ms ease,
                    padding 220ms ease;
                z-index: 70;
            }

            [data-web-template="studio-clean"] [data-web-region="header"][data-studio-header-scrolled] {
                box-shadow: 0 18px 42px rgba(17,19,21,.16);
                min-height: 64px !important;
                padding-block: 8px !important;
            }

            [data-web-template="studio-clean"] [data-document-role="headerNavigation"] {
                gap: clamp(.35rem, .65vw, .65rem) !important;
                justify-self: center;
            }

            [data-web-template="studio-clean"] [data-document-role="headerNavigation"] .site-builder-text,
            [data-web-template="studio-clean"] [data-document-role="headerNavigation"] .site-builder-navigation-link {
                display: inline-flex;
                font-size: .7rem;
                font-weight: 850;
                letter-spacing: .1em;
                position: relative;
                text-transform: uppercase;
                width: auto !important;
            }

            [data-web-template="studio-clean"] [data-document-role="headerActions"] {
                flex-wrap: nowrap;
                justify-content: flex-end;
            }

            [data-web-template="studio-clean"] [data-web-region="studioHero"] {
                overflow: hidden;
            }

            [data-web-template="studio-clean"] [data-web-region="studioHero"] > .site-builder-grid {
                align-items: stretch;
                gap: 0;
            }

            [data-web-template="studio-clean"] [data-web-region="studioHero"] [data-document-role="heroCopy"] {
                align-content: center;
                min-height: min(820px, calc(100svh - 112px));
                position: relative;
                z-index: 2;
            }

            [data-web-template="studio-clean"] [data-web-region="studioHero"] [data-document-role="heroCopy"]::after {
                background: var(--site-accent);
                bottom: clamp(2rem, 5vw, 5rem);
                content: "";
                height: 4px;
                position: absolute;
                right: clamp(1.5rem, 4vw, 4rem);
                width: clamp(58px, 8vw, 110px);
            }

            [data-web-template="studio-clean"] [data-document-role="heroVisual"] {
                isolation: isolate;
                overflow: hidden;
                position: relative;
            }

            [data-web-template="studio-clean"] [data-document-role="heroVisual"]::before {
                background: var(--site-primary);
                clip-path: polygon(0 0, 100% 0, 0 100%);
                content: "";
                height: 100%;
                left: -1px;
                pointer-events: none;
                position: absolute;
                top: 0;
                width: clamp(44px, 5vw, 80px);
                z-index: 2;
            }

            [data-web-template="studio-clean"] [data-document-role="heroVisual"] .site-builder-image,
            [data-web-template="studio-clean"] [data-document-role="editorialVisual"] .site-builder-image {
                filter: grayscale(.9) contrast(1.08);
                min-height: 100%;
                transform: scale(1.015);
                transition: filter 680ms ease, transform 1000ms cubic-bezier(.2,.75,.25,1);
            }

            [data-web-template="studio-clean"] [data-document-role="heroVisual"]:hover .site-builder-image,
            [data-web-template="studio-clean"] [data-document-role="editorialVisual"]:hover .site-builder-image {
                filter: grayscale(0) contrast(1.03);
                transform: scale(1.05);
            }

            [data-web-template="studio-clean"] [data-document-role="editorialVisual"] {
                overflow: hidden;
                position: relative;
            }

            [data-web-template="studio-clean"] [data-web-region$="Hero"]:not([data-web-region="studioHero"]) {
                background-image:
                    linear-gradient(115deg, transparent 0 70%, color-mix(in srgb, var(--site-accent) 15%, transparent) 70% 70.4%, transparent 70.4%),
                    linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px) !important;
                background-size: auto, 100% 32px !important;
                border-bottom: 4px solid var(--site-accent);
            }

            [data-web-template="studio-clean"] [data-web-region="studioPrinciples"] .site-builder-card,
            [data-web-template="studio-clean"] [data-web-region="studioCapabilities"] .site-builder-card,
            [data-web-template="studio-clean"] [data-web-region="servicesGrid"] .site-builder-card,
            [data-web-template="studio-clean"] [data-web-region="storyValues"] .site-builder-card {
                border-width: 3px 0 0 0 !important;
                min-height: 100%;
            }

            [data-web-template="studio-clean"] [data-web-region="studioProcess"] [data-document-role="editorialVisual"]::after,
            [data-web-template="studio-clean"] [data-web-region="storyTeam"] [data-document-role="editorialVisual"]::after {
                border: 1px solid var(--site-accent);
                content: "";
                inset: 1rem -1rem -1rem 1rem;
                pointer-events: none;
                position: absolute;
                z-index: -1;
            }

            [data-web-template="studio-clean"] [data-web-region$="Cta"],
            [data-web-template="studio-clean"] [data-web-region="contactBrief"] {
                border-top: 4px solid var(--site-accent);
            }

            [data-web-template="studio-clean"] [data-web-region="footer"] {
                border-top: 4px solid var(--site-accent);
            }

            [data-web-template="studio-clean"] [data-web-region="footer"] a {
                text-decoration-color: color-mix(in srgb, var(--site-accent) 70%, transparent);
                text-underline-offset: .25em;
            }

            [data-web-template="studio-clean"]:not([data-web-template-editor="true"])[data-studio-runtime-ready] [data-studio-reveal] {
                opacity: 0;
                transform: translateY(26px);
                transition:
                    opacity 620ms cubic-bezier(.2,.75,.25,1),
                    transform 700ms cubic-bezier(.2,.75,.25,1);
            }

            [data-web-template="studio-clean"]:not([data-web-template-editor="true"])[data-studio-runtime-ready] [data-studio-reveal][data-studio-revealed] {
                opacity: 1;
                transform: translateY(0);
            }

            [data-web-template="studio-clean"] .studio-mobile-menu-toggle {
                display: none;
            }

            @container (max-width: 900px) {
                [data-web-template="studio-clean"] [data-web-region="studioServiceStrip"] {
                    display: none;
                }

                [data-web-template="studio-clean"] [data-web-region="header"] {
                    min-height: 74px;
                    padding-inline: 1rem;
                    top: 0;
                }

                [data-web-template="studio-clean"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="studio-clean"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] > .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="studio-clean"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="studio-clean"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    display: block;
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template="studio-clean"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="studio-clean"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: none;
                }

                [data-web-template="studio-clean"] [data-web-region="header"] .studio-mobile-menu-toggle {
                    align-items: center;
                    background: var(--site-primary);
                    border: 1px solid var(--site-primary);
                    cursor: pointer;
                    display: grid;
                    gap: 6px;
                    height: 44px;
                    justify-self: end;
                    padding: 10px;
                    place-content: center;
                    position: absolute;
                    right: 1rem;
                    top: 14px;
                    width: 44px;
                    z-index: 3;
                }

                [data-web-template="studio-clean"] .studio-mobile-menu-toggle > span {
                    background: #ffffff;
                    display: block;
                    height: 2px;
                    transition: opacity 180ms ease, transform 180ms ease;
                    width: 23px;
                }

                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] {
                    background: var(--site-background) !important;
                    bottom: 0;
                    left: 0;
                    overflow-y: auto;
                    position: fixed;
                    right: 0;
                    top: 0;
                    z-index: 90;
                }

                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] > .site-builder-grid {
                    align-content: start;
                    min-height: 100%;
                    padding-bottom: 2rem;
                }

                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: block;
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    grid-row: 2 !important;
                    margin-top: clamp(3rem, 9vh, 5rem);
                }

                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-row: 3 !important;
                }

                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] [data-document-role="headerNavigation"],
                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] [data-document-role="headerActions"] {
                    align-items: stretch;
                    flex-direction: column;
                    gap: 0 !important;
                }

                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                    border-bottom: 1px solid var(--site-border);
                    padding-block: 1rem;
                    width: 100%;
                }

                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text {
                    display: block;
                    font-family: var(--site-heading-font) !important;
                    font-size: clamp(2.2rem, 10vw, 3.6rem) !important;
                    font-weight: 850 !important;
                    letter-spacing: -.025em !important;
                    width: 100% !important;
                }

                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] [data-document-role="headerActions"] {
                    gap: .75rem !important;
                    margin-top: 1rem;
                }

                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] [data-document-role="headerActions"] > * {
                    width: 100% !important;
                }

                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] .studio-mobile-menu-toggle > span:first-child {
                    transform: translateY(8px) rotate(45deg);
                }

                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] .studio-mobile-menu-toggle > span:nth-child(2) {
                    opacity: 0;
                }

                [data-web-template="studio-clean"] [data-web-region="header"][data-studio-mobile-menu-open] .studio-mobile-menu-toggle > span:last-child {
                    transform: translateY(-8px) rotate(-45deg);
                }

                [data-web-template="studio-clean"] [data-web-region="studioHero"] > .site-builder-grid {
                    gap: 0;
                }

                [data-web-template="studio-clean"] [data-web-region="studioHero"] [data-document-role="heroCopy"] {
                    min-height: auto;
                    padding: clamp(48px, 11vw, 76px) clamp(24px, 7vw, 52px) !important;
                }

                [data-web-template="studio-clean"] [data-document-role="heroVisual"] {
                    min-height: 520px;
                }

                [data-web-template="studio-clean"] [data-document-role="heroVisual"] .site-builder-image {
                    min-height: 520px !important;
                }

                [data-web-template="studio-clean"] [data-document-role="heroVisual"]::before {
                    height: clamp(36px, 8vw, 60px);
                    left: 0;
                    top: -1px;
                    width: 100%;
                }
            }

            @container (max-width: 640px) {
                [data-web-template="studio-clean"] .site-builder-section,
                [data-web-template="studio-clean"] [data-site-builder-block="footer"] {
                    padding-inline: 1rem;
                }

                [data-web-template="studio-clean"] .site-builder-text-h1 {
                    font-size: clamp(3.35rem, 15vw, 5rem) !important;
                }

                [data-web-template="studio-clean"] .site-builder-text-h2 {
                    font-size: clamp(2.55rem, 11.5vw, 4rem) !important;
                }

                [data-web-template="studio-clean"] [data-document-role="heroVisual"],
                [data-web-template="studio-clean"] [data-document-role="heroVisual"] .site-builder-image {
                    min-height: 440px !important;
                }

                [data-web-template="studio-clean"] .site-builder-button {
                    width: 100% !important;
                }
            }

            @container (max-width: 430px) {
                [data-web-template="studio-clean"] [data-web-region="studioHero"] .site-builder-text-h1 {
                    font-size: clamp(2.4rem, 5cqw, 3.25rem) !important;
                    hyphens: none;
                    overflow-wrap: normal !important;
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="studio-clean"],
                [data-web-template="studio-clean"] * {
                    scroll-behavior: auto !important;
                }

                [data-web-template="studio-clean"] .site-builder-button,
                [data-web-template="studio-clean"] .site-builder-card,
                [data-web-template="studio-clean"] .site-builder-image,
                [data-web-template="studio-clean"] [data-studio-reveal] {
                    transition-duration: .01ms !important;
                }

                [data-web-template="studio-clean"] [data-studio-reveal] {
                    opacity: 1 !important;
                    transform: none !important;
                }

                [data-web-template="studio-clean"] .site-builder-image {
                    transform: none !important;
                }
            }
        `}</style>
    );
}

function StudioCleanFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) return null;

    return (
        <main className="px-5 py-24 sm:px-6" data-builder-document-region="page">
            <article className="mx-auto max-w-[920px] leading-8 text-[var(--site-muted)] [&_a]:text-[var(--site-accent)] [&_blockquote]:border-l-4 [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-8 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-6xl [&_h1]:font-black [&_h1]:uppercase [&_h1]:leading-[.82] [&_h1]:text-[var(--site-text)] [&_h2]:mb-4 [&_h2]:mt-12 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-4xl [&_h2]:font-black [&_h2]:uppercase [&_h2]:text-[var(--site-text)] [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-[var(--site-text)] [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(
                    page,
                    design,
                    LinkComponent,
                    props.leadForm,
                    props.readOnly,
                )}
            </article>
        </main>
    );
}

export const studioCleanTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "studio-clean",
        name: "Kreativní studio a agentura",
        type: "website",
        category: "business",
        description: "Výrazná editoriální šablona pro kreativní studio a agenturu s ostrou typografií, obrazovým portfoliem a přesným projektovým rytmem.",
        previewImage: "/templates/studio-clean/preview.jpg",
        defaults: webSystemTemplateDesignDefaults["studio-clean"],
        pageTypes: {
            home: { label: "Studio Clean homepage", slots: ["richContent"] },
            content: { label: "Studio Clean content page", slots: ["richContent"] },
            contact: { label: "Studio Clean contact page", slots: ["richContent"] },
        },
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["solid", "outline"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(
            studioCleanTemplate.manifest.defaults,
            props.designOverride ?? props.site?.templateDesign,
        );
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                className="min-h-screen max-w-full overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                data-web-template="studio-clean"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                style={{
                    ...templateDesignToCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <StudioCleanStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <StudioCleanRuntime
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={(
                        <StudioCleanFallbackPage
                            design={design}
                            LinkComponent={LinkComponent}
                            props={props}
                        />
                    )}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
